/**
 * UI-6b Task 17 — `useDefaultDashboard` hook.
 *
 * Thin wrapper around `GET /dashboards/default?role=<role>`. The endpoint
 * returns either a dashboard row (the role's `is_default=true` row) or `null`
 * when no default is configured. Consumers (e.g. SupervisorDashboard's
 * default-dashboard switch in Task 18) need both states:
 *
 *   const { dashboard, loading } = useDefaultDashboard('supervisor')
 *   if (loading) return <Spinner />
 *   return dashboard ? <DashboardRenderer layout={dashboard.layout} /> : <Legacy />
 *
 * Design notes:
 *   - The hook owns its own fetch (no shared store) — there's at most one
 *     "default dashboard for this role" per landing-page mount, so a
 *     dedicated store would just be ceremony.
 *   - `refetch` is exposed so the composer's "Set as default" flow can poke
 *     consumers to re-read the new default if they're mounted side-by-side
 *     during a dev preview. The supervisor landing page doesn't use it.
 *   - Errors are surfaced verbatim on the `error` field; the caller decides
 *     whether to fall back, retry, or show a toast.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type { DashboardLayout } from '../components/dashboards/widgets/types.js'

export interface Dashboard {
  id: string
  role: string
  name: string
  isDefault: boolean
  layout: DashboardLayout
  createdAt: string
  updatedAt: string
}

export interface UseDefaultDashboardResult {
  dashboard: Dashboard | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useDefaultDashboard(role: string): UseDefaultDashboardResult {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Mounted guard prevents setState after unmount when an in-flight fetch
  // resolves late (same pattern as ContactsPage's local-fetch list).
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (!role) return
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<Dashboard | null>(
        `/dashboards/default?role=${encodeURIComponent(role)}`,
      )
      if (mountedRef.current) {
        setDashboard(resp)
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load default dashboard')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [role])

  useEffect(() => {
    void load()
  }, [load])

  return { dashboard, loading, error, refetch: load }
}
